import React from "react";
import { motion } from "framer-motion";
import { TIERS, DESKTOP_ORDER, MOBILE_ORDER } from "../data/tiers";
import { PricingCard } from "./PricingCard";
import { useAvailability } from "../hooks/useAvailability";

const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 } },
};

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.45 + i * 0.12 } }),
};

const HeroHeader = () => (
  <header className="relative z-20 pt-8 pb-4 flex justify-center" data-testid="site-header">
    <img
      src="/assets/logo-white.png"
      alt="The Biohacker Network"
      className="h-9 sm:h-11 w-auto"
      data-testid="header-logo"
    />
  </header>
);

const HeroHeadline = () => (
  <div className="text-center">
    <motion.p
      className="eyebrow text-cyan mb-5"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.05 }}
      data-testid="hero-eyebrow"
    >
      Private Membership
    </motion.p>

    <h1 className="font-display font-700 tracking-tighter leading-[1.02] text-3xl sm:text-4xl lg:text-5xl">
      <span className="block overflow-hidden">
        <motion.span className="block" variants={line} initial="hidden" animate="show">
          <span className="text-white">MEMBERSHIP THAT </span>
          <span className="bg-gradient-to-r from-[#00D4FF] to-[#00E676] bg-clip-text text-transparent">
            PAYS YOU BACK.
          </span>
        </motion.span>
      </span>
    </h1>

    <motion.p
      className="mt-5 text-sm sm:text-base text-dim font-light"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.35 }}
      data-testid="hero-subheadline"
    >
      Unlock monthly store credit, member pricing and premium benefits. Choose your level.
    </motion.p>
  </div>
);

const HeroCards = () => {
  const availability = useAvailability();
  const card = (id, i) => (
    <motion.div key={id} variants={reveal} custom={i} initial="hidden" animate="show">
      <PricingCard tier={TIERS[id]} availability={availability?.[TIERS[id].lookupKey]} />
    </motion.div>
  );
  return (
    <div className="mt-10 sm:mt-12 w-full max-w-6xl mx-auto" data-testid="membership-cards">
      <div className="hidden lg:grid grid-cols-3 gap-5 items-stretch">
        {DESKTOP_ORDER.map(card)}
      </div>
      <div className="lg:hidden flex flex-col gap-6 max-w-md mx-auto">
        {MOBILE_ORDER.map(card)}
      </div>
    </div>
  );
};

const HeroTagline = () => (
  <motion.div
    className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-3 font-display text-xs sm:text-sm tracking-[0.22em] text-white/70"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.9, delay: 0.9 }}
  >
    <span>THE MORE YOU OPTIMIZE.</span>
    <span className="hidden sm:inline text-cyan">/</span>
    <span>THE MORE YOU UNLOCK.</span>
  </motion.div>
);

export const Hero = () => {
  return (
    <section className="relative flex flex-col grain pb-16 sm:pb-20" data-testid="hero-section">
      <HeroHeader />
      <div className="divider-line max-w-6xl mx-auto w-full" />
      <div className="relative z-10 px-5 pt-10 sm:pt-12">
        <HeroHeadline />
        <HeroCards />
        <HeroTagline />
      </div>
    </section>
  );
};
